import { forwardRef } from 'react';
import { useInView } from 'react-intersection-observer';

import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { DateTime } from 'luxon';
import Image from 'next/image';
import MealCard from 'src/components/MealCard';

import { FunnelCartDelivery } from 'src/ts/types/cart';
import { CatalogsQuery } from '../../ts/@types/generated/graphql/catalog-service/graphql';
import EstimatedDeliveryTooltip from '../EstimatedDeliveryTooltip';

export interface CatalogProps {
  catalog: CatalogsQuery['catalogs'][0];
  featuredSkuGroups: string[][];
  cartDelivery?: FunnelCartDelivery;
  addMealToCart(variantId: number): void;
  removeMealFromCart(variantId: number): void;
  setAsSelected: () => void;
  isRecurring?: boolean;
  mealDetailsModalOptions?: {
    enableMealDetailsModal: boolean;
    tagFilterType: 'funnel' | 'postCheckout';
  };
  totalCatalogs: number;
}

const Catalog = forwardRef<HTMLDivElement, CatalogProps>(function Catalog(
  {
    catalog,
    featuredSkuGroups,
    cartDelivery,
    addMealToCart,
    removeMealFromCart,
    setAsSelected,
    isRecurring,
    mealDetailsModalOptions,
    totalCatalogs,
  },
  ref,
) {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );
  const deliveryDate = DateTime.fromISO(
    // This date fallback logic is only needed for the blurred "loading" catalog displayed while the zip modal is open since no cart exists yet.
    cartDelivery?.estimatedDeliveryDate || catalog.eligibleDeliveryDates[0],
    {
      zone: 'utc',
    },
  );
  const formattedDeliveryDateString = deliveryDate.toLocaleString({
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const { items: itemsToDisplay } = catalog;

  const { ref: inViewRef } = useInView({
    onChange: (inView) => {
      if (!isSmallScreen && inView) {
        // Mobile UX has a tabbed experience instead of a jump-scroll experience, so
        //   we don't need to react to scroll-intersection events.
        setAsSelected();
      }
    },
    threshold: 0,
  });

  const refHandler = (el: any) => {
    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
    inViewRef(el);
  };

  return (
    <Box
      ref={refHandler}
      sx={{
        ...(isSmallScreen
          ? { paddingTop: '2rem', paddingLeft: 0, paddingRight: 0 }
          : {
              // Creates a bit of space between the delivery date anchor, when present, on smaller screens
              paddingLeft: totalCatalogs > 1 ? '16px' : 0,
            }),
      }}
    >
      {!isSmallScreen && (
        <>
          {cartDelivery?.estimatedTransitTime != null && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                }}
              >
                Est. Delivery
              </Typography>
              <EstimatedDeliveryTooltip fontSize="16px" />
            </Box>
          )}
          <Typography variant="h5" gutterBottom component="div">
            {formattedDeliveryDateString}
          </Typography>
        </>
      )}
      <Grid
        container
        sx={
          isSmallScreen
            ? {}
            : { justifyContent: 'space-between', alignItems: 'center' }
        }
        rowSpacing={isSmallScreen ? 2 : 4}
      >
        {itemsToDisplay?.length > 0 ? (
          itemsToDisplay.map((catalogItem) => {
            if (!catalogItem) {
              return null;
            }
            // calculate quantity of this catalogItem (meal) for the appropriate cartDelivery
            // if there is no cartDelivery, then the quantity is 0
            const quantityInCart =
              cartDelivery?.lineItems?.reduce((totalQuantity, lineItem) => {
                if (lineItem.variantId === catalogItem.variant.id) {
                  return totalQuantity + lineItem.quantity;
                }
                return totalQuantity;
              }, 0) || 0;

            const {
              spicyTags,
              servingsTags,
              celebrityChefTags,
              territoryServesTags,
              newTags,
              improvedRecipeTags,
              seasonalTags,
            } = catalogItem.variant;

            return (
              <Grid
                key={catalogItem.id}
                sx={{
                  marginTop: 0,
                  display: isSmallScreen ? 'flex' : undefined,
                  justifyContent: isSmallScreen ? 'center' : undefined,
                  alignSelf: 'stretch',
                }}
                size={isSmallScreen ? 6 : undefined}
              >
                {catalogItem.variant.__typename === 'MealVariant' && (
                  <MealCard
                    mealVariantData={catalogItem.variant}
                    mealPrice={
                      isRecurring
                        ? catalogItem.priceInCents
                        : catalogItem.oneTimeOrderPriceInCents
                    }
                    mealTags={{
                      spicyTags,
                      servingsTags,
                      celebrityChefTags,
                      territoryServesTags,
                      newTags,
                      improvedRecipeTags,
                      seasonalTags,
                    }}
                    soldOut={catalogItem.soldOut}
                    quantityInCart={quantityInCart}
                    editCartFunctions={{ addMealToCart, removeMealFromCart }}
                    mealDetailsModalOptions={mealDetailsModalOptions}
                    featuredSkuGroups={featuredSkuGroups}
                  />
                )}
              </Grid>
            );
          })
        ) : (
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column',
              flexWrap: 'wrap',
              alignItems: 'center',
              width: '100%',
              marginTop: isSmallScreen ? '3rem' : undefined,
            }}
          >
            <Image
              src={'/static/images/catalog-empty-state.svg'}
              alt="plate-icon"
              height={82}
              width={82}
            />
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
            >
              We couldn’t find any meals matching your preferences.
              {!isSmallScreen && <br />}
              Try adjusting your filters for more results.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
});

export default Catalog;
